import { useEffect, useState } from "react";
import { api } from "../api/apiClient";
import type {
  TelegramAccountOption,
  TelegramPeople,
  TelegramPersonRow,
  TelegramStatus,
} from "../api/types";

function errText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

const roleLabel = (role: string): string => role.charAt(0) + role.slice(1).toLowerCase();

const yesNo = (value: boolean | null): string =>
  value == null ? "unknown" : value ? "yes" : "no";

/**
 * G-122 (OD-130…OD-134, OD-136, OD-137): what the staff bot sends from the
 * CRM, and to whom. New Shopify orders go to the staff group; the 21:00
 * Shopify total goes privately to each active owner and admin linked here.
 * Who is an owner, admin or manager is the CRM's own role (Manage team),
 * never chosen on this page; the accounts come from the staff bot's roster.
 */
export function TelegramPage() {
  const [status, setStatus] = useState<TelegramStatus | null>(null);
  const [people, setPeople] = useState<TelegramPeople | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    Promise.all([api.getTelegramStatus(), api.listTelegramPeople()])
      .then(([nextStatus, nextPeople]) => {
        if (cancelled) return;
        setStatus(nextStatus);
        setPeople(nextPeople);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errText(e, "Could not load the Telegram settings"));
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  return (
    <section className="page-stack">
      <div className="page-header">
        <div>
          <h2 className="page-title">Telegram</h2>
          <p className="page-subtitle">
            The staff bot posts new Shopify orders to the staff group and sends the 21:00 Shopify
            total to owners and admins.
          </p>
        </div>
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      {status && !status.botConfigured ? (
        <div className="alert warning">
          The staff bot&apos;s token (STAFF_BOT_TOKEN) is not set on the server, so nothing is sent
          to Telegram. Copy it, and STAFF_GROUP_ID, from the staff bot&apos;s settings.
        </div>
      ) : null}
      <GroupCard status={status} onReload={() => setReloadTick((n) => n + 1)} />
      <PeopleCard status={status} people={people} onPeople={setPeople} />
    </section>
  );
}

function GroupCard({
  status,
  onReload,
}: {
  status: TelegramStatus | null;
  onReload: () => void;
}) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const test = async () => {
    setTesting(true);
    setResult(null);
    try {
      const sent = await api.testTelegramGroup();
      setResult(
        sent.ok
          ? { ok: true, text: "Test message posted in the group's General topic." }
          : { ok: false, text: sent.problem ?? "Telegram refused the message." },
      );
    } catch (e) {
      setResult({ ok: false, text: errText(e, "Could not send the test message") });
    } finally {
      setTesting(false);
    }
  };

  const group = status?.group;
  return (
    <div className="card telegram-card">
      <div>
        <h3>New Shopify orders → staff group</h3>
        <p className="muted">
          Each order the CRM shows gets its own topic with the details, and one line in General
          saying which shop can fulfill it. The topic is closed when the order is fulfilled or
          cancelled.
        </p>
      </div>
      {status ? (
        <ul className="telegram-facts">
          <li>
            Bot:{" "}
            {status.botUsername ? `@${status.botUsername}` : status.botProblem ?? "not reachable"}
          </li>
          <li>
            Group:{" "}
            {group?.reachable
              ? group.title ?? "reachable"
              : group?.problem ?? "not checked"}
          </li>
          {group?.reachable ? (
            <>
              <li>Topics on: {yesNo(group.topicsEnabled)}</li>
              <li>Bot may manage topics: {yesNo(group.canManageTopics)}</li>
            </>
          ) : null}
        </ul>
      ) : (
        <p className="muted">Checking with Telegram…</p>
      )}
      {group?.reachable && group.problem ? (
        <div className="alert warning">{group.problem}</div>
      ) : null}
      {result ? (
        <div className={result.ok ? "alert success" : "alert error"}>{result.text}</div>
      ) : null}
      <div className="button-row">
        <button
          type="button"
          className="button"
          disabled={testing || !group?.configured || !status?.botConfigured}
          onClick={() => void test()}
        >
          {testing ? "Sending..." : "Send test message"}
        </button>
        <button type="button" className="button tinted" onClick={onReload}>
          Check again
        </button>
      </div>
    </div>
  );
}

const OTHER_ID = "__other";

/** "Ana Ruiz · @ana_flowers · Còrsega · 222222", plus why it may not work. */
function accountLabel(option: TelegramAccountOption): string {
  const name = [option.firstName, option.lastName].filter(Boolean).join(" ");
  return [
    name || null,
    option.username ? `@${option.username}` : null,
    option.shop,
    option.isBotOwner ? "staff bot owner" : null,
    option.telegramUserId,
    option.inGroup === false ? "not in the staff group" : null,
    option.fromBot ? null : "typed by hand",
  ]
    .filter(Boolean)
    .join(" · ");
}

const receivesText: Record<TelegramPersonRow["receives"], string> = {
  DAILY_TOTAL: "Gets the 21:00 total",
  NOTHING_YET: "Nothing to receive yet",
  NOTHING_INACTIVE: "Inactive: receives nothing",
};

/**
 * G-122 S2 (OD-136, OD-137): one row per owner, admin and manager, each with
 * a dropdown of the accounts the staff bot knows. Choosing saves at once;
 * "Other ID…" is for someone the bot does not list.
 */
function PeopleCard({
  status,
  people,
  onPeople,
}: {
  status: TelegramStatus | null;
  people: TelegramPeople | null;
  onPeople: (next: TelegramPeople) => void;
}) {
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [typingFor, setTypingFor] = useState<string | null>(null);
  const [typedId, setTypedId] = useState("");
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const run = async (
    userId: string,
    work: () => Promise<{ ok: boolean; text: string } | null>,
  ) => {
    setBusyUserId(userId);
    setMessage(null);
    try {
      setMessage(await work());
    } catch (e) {
      setMessage({ ok: false, text: errText(e, "Something went wrong") });
    } finally {
      setBusyUserId(null);
    }
  };

  const choose = (row: TelegramPersonRow, value: string) => {
    if (value === OTHER_ID) {
      setTypingFor(row.userId);
      setTypedId("");
      return;
    }
    setTypingFor(null);
    void run(row.userId, async () => {
      if (value === "") {
        onPeople(await api.unlinkTelegram(row.userId));
        return null;
      }
      onPeople(await api.linkTelegram({ userId: row.userId, telegramUserId: value }));
      return { ok: true, text: `${row.fullName} linked. Send a test message to check it arrives.` };
    });
  };

  const saveTyped = (row: TelegramPersonRow) =>
    run(row.userId, async () => {
      onPeople(await api.linkTelegram({ userId: row.userId, telegramUserId: typedId.trim() }));
      setTypingFor(null);
      return { ok: true, text: `${row.fullName} linked. Send a test message to check it arrives.` };
    });

  const test = (row: TelegramPersonRow) =>
    run(row.userId, async () => {
      const sent = await api.testTelegramLink(row.userId);
      return sent.ok
        ? { ok: true, text: `Test message delivered to ${row.fullName}.` }
        : { ok: false, text: `${row.fullName}: ${sent.problem ?? "Telegram refused the message."}` };
    });

  const linkedBy = new Map(
    (people?.rows ?? [])
      .filter((row) => row.telegramUserId)
      .map((row) => [row.telegramUserId as string, row.fullName]),
  );
  const last = status?.lastDailyReport;
  const bot = status?.botUsername ? `@${status.botUsername}` : "the staff bot";

  return (
    <div className="card telegram-card">
      <div>
        <h3>People</h3>
        <p className="muted">
          Pick each person&apos;s Telegram account from {bot}&apos;s staff list. Owners and admins
          get the Shopify total privately every evening at 21:00 (Madrid), covering sales since
          21:00 the day before; managers can be linked now and receive nothing privately yet.
        </p>
        <p className="muted">
          {people?.rosterReceivedAt
            ? `Staff list from ${bot}, received ${new Date(people.rosterReceivedAt).toLocaleString()}.`
            : null}
          {last
            ? ` Last 21:00 total: ${new Date(last.windowEnd).toLocaleString()}, ${last.orderCount} order${
                last.orderCount === 1 ? "" : "s"
              }, €${last.totalEur}, delivered to ${last.sentCount}${
                last.failedCount > 0 ? `, ${last.failedCount} failed` : ""
              }${last.staleSince ? " (Shopify was unreachable)" : ""}.`
            : " The 21:00 total has not been sent yet."}
        </p>
      </div>

      {people && !people.rosterReceivedAt ? (
        <div className="alert warning">
          {bot} has not sent its staff list yet, so the lists below are empty. Use &quot;Other
          ID…&quot; to type an ID for now, or check that STAFF_ROSTER_SECRET is the same on both
          servers.
        </div>
      ) : null}

      {message ? (
        <div className={message.ok ? "alert success" : "alert error"}>{message.text}</div>
      ) : null}

      {people ? (
        <ul className="telegram-people">
          {people.rows.map((row) => {
            const busy = busyUserId === row.userId;
            const typing = typingFor === row.userId;
            return (
              <li key={row.userId}>
                <div className="telegram-person">
                  <strong>{row.fullName}</strong>
                  <span className="hint">
                    {roleLabel(row.role)} · {receivesText[row.receives]}
                  </span>
                </div>
                <div className="field telegram-person-pick">
                  <label className="visually-hidden" htmlFor={`tg-${row.userId}`}>
                    Telegram account for {row.fullName}
                  </label>
                  <select
                    id={`tg-${row.userId}`}
                    value={typing ? OTHER_ID : row.telegramUserId ?? ""}
                    disabled={busy}
                    onChange={(e) => choose(row, e.target.value)}
                  >
                    <option value="">Not linked</option>
                    {people.options.map((option) => {
                      const holder = linkedBy.get(option.telegramUserId);
                      const takenByOther = holder != null && option.telegramUserId !== row.telegramUserId;
                      return (
                        <option
                          key={option.telegramUserId}
                          value={option.telegramUserId}
                          disabled={takenByOther}
                        >
                          {accountLabel(option)}
                          {takenByOther ? ` (linked to ${holder})` : ""}
                        </option>
                      );
                    })}
                    <option value={OTHER_ID}>Other ID…</option>
                  </select>
                  {typing ? (
                    <div className="button-row">
                      <input
                        aria-label={`Telegram ID for ${row.fullName}`}
                        value={typedId}
                        onChange={(e) => setTypedId(e.target.value)}
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="e.g. 123456789 (from @userinfobot)"
                      />
                      <button
                        type="button"
                        className="button"
                        disabled={busy || !/^\d{5,16}$/.test(typedId.trim())}
                        onClick={() => void saveTyped(row)}
                      >
                        Save
                      </button>
                      <button type="button" className="button plain" onClick={() => setTypingFor(null)}>
                        Cancel
                      </button>
                    </div>
                  ) : null}
                </div>
                {row.telegramUserId && !typing ? (
                  <button
                    type="button"
                    className="button tinted"
                    disabled={busy}
                    onClick={() => void test(row)}
                  >
                    Send test
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
